import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { registrosApi } from '../../api/registrosApi';

const crearIdempotencyKey = () =>
  `cantina-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function useCantinaCheckout({
  carrito,
  total,
  onSuccess,
}) {
  const [abierto, setAbierto] = useState(false);
  const [mediosPago, setMediosPago] = useState([]);
  const [medioSeleccionado, setMedioSeleccionado] = useState(null);
  const [dividir, setDividir] = useState(false);
  const [medioSeleccionado2, setMedioSeleccionado2] = useState(null);
  const [monto1, setMonto1] = useState('');
  const [monto2, setMonto2] = useState('');
  const [password, setPassword] = useState('');
  const [cargandoMedios, setCargandoMedios] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const cargarMedios = async () => {
      try {
        setCargandoMedios(true);

        const response = await registrosApi.getMediosDePago();

        const data = Array.isArray(response.data)
          ? response.data
          : [];

        setMediosPago(data);

        if (data.length > 0) {
          setMedioSeleccionado(data[0].id);
        }
      } catch (err) {
        console.error('Error cargando medios de pago:', err);
        setMediosPago([]);
      } finally {
        setCargandoMedios(false);
      }
    };

    cargarMedios();
  }, []);

  // Si el primer medio pasa a ser el mismo que el segundo, se limpia el segundo.
  useEffect(() => {
    if (medioSeleccionado2 && medioSeleccionado2 === medioSeleccionado) {
      setMedioSeleccionado2(null);
    }
  }, [medioSeleccionado, medioSeleccionado2]);

  const reiniciarDivision = () => {
    setDividir(false);
    setMedioSeleccionado2(null);
    setMonto1('');
    setMonto2('');
  };

  // Los dos montos suman el total: al escribir uno, el otro se completa
  // solo con lo que resta. Todo en centavos para no arrastrar decimales.
  const totalCentavos = Math.round(Number(total || 0) * 100);

  const aCentavos = (texto) =>
    Math.round(Number(String(texto).replace(',', '.')) * 100);

  const montoDivisionValido = (centavos) =>
    Number.isFinite(centavos) && centavos > 0 && centavos < totalCentavos;

  const aTexto = (centavos) => {
    const pesos = centavos / 100;

    return (Number.isInteger(pesos) ? String(pesos) : pesos.toFixed(2)).replace(
      '.',
      ','
    );
  };

  const cambiarMonto1 = (texto) => {
    setMonto1(texto);

    const centavos = aCentavos(texto);

    setMonto2(
      montoDivisionValido(centavos) ? aTexto(totalCentavos - centavos) : ''
    );
  };

  const cambiarMonto2 = (texto) => {
    setMonto2(texto);

    const centavos = aCentavos(texto);

    setMonto1(
      montoDivisionValido(centavos) ? aTexto(totalCentavos - centavos) : ''
    );
  };

  const monto1Centavos = aCentavos(monto1);
  const monto2Centavos = aCentavos(monto2);
  const montoValido =
    montoDivisionValido(monto1Centavos) &&
    montoDivisionValido(monto2Centavos) &&
    monto1Centavos + monto2Centavos === totalCentavos;
  const monto2Calculado = montoValido ? monto2Centavos / 100 : null;

  const alternarDivision = () => {
    if (dividir) {
      reiniciarDivision();
    } else {
      setDividir(true);
    }

    setError('');
  };

  const abrir = () => {
    if (!carrito.length) return;

    setError('');
    setPassword('');
    reiniciarDivision();

    if (mediosPago.length > 0 && !medioSeleccionado) {
      setMedioSeleccionado(mediosPago[0].id);
    }

    setAbierto(true);
  };

  const cerrar = () => {
    if (procesando) return;

    setAbierto(false);
    setPassword('');
    setError('');
    reiniciarDivision();
  };

  const seleccionarPorIndice = (indice) => {
    const medio = mediosPago[indice];

    if (medio) {
      setMedioSeleccionado(medio.id);
    }
  };

  const confirmar = async () => {
    if (procesando) return;

    if (!medioSeleccionado) {
      setError('Seleccioná un medio de pago.');
      return;
    }

    let montoSegundo = null;

    if (dividir) {
      if (!montoValido) {
        setError(
          'Ingresá el monto de cada medio: cada uno más que cero y menos que el total, y entre los dos suman el total.'
        );
        return;
      }

      if (!medioSeleccionado2) {
        setError('Elegí el segundo medio de pago.');
        return;
      }

      montoSegundo = monto2Calculado;
    }

    const passwordLimpia = password.trim();

    if (!passwordLimpia) {
      setError('Ingresá tu PIN para confirmar la venta.');
      return;
    }

    try {
      setProcesando(true);
      setError('');

      const authResponse = await registrosApi.verifyPassword({
        password: passwordLimpia,
      });

      const usuario = authResponse?.data?.usuario;

      if (!authResponse?.data?.esValida || !usuario?.id) {
        setError('PIN inválido.');
        return;
      }

      const ventaResponse = await api.post('/cantina/ventas', {
        usuario_id: usuario.id,
        medio_pago_id: medioSeleccionado,
        ...(dividir && {
          medio_pago_id_2: medioSeleccionado2,
          monto_medio_pago_2: montoSegundo,
        }),
        idempotency_key: crearIdempotencyKey(),
        items: carrito.map((item) => ({
          producto_id: item.id,
          cantidad: item.cantidad,
        })),
      });

      setAbierto(false);
      setPassword('');
      setError('');
      reiniciarDivision();

      onSuccess?.({
        venta: ventaResponse.data,
        usuario,
      });
    } catch (err) {
      console.error('Error cobrando venta de Cantina:', err);

      setError(
        err?.response?.data?.error ||
          'No se pudo registrar la venta.'
      );
    } finally {
      setProcesando(false);
    }
  };

  return {
    abierto,
    mediosPago,
    medioSeleccionado,
    dividir,
    medioSeleccionado2,
    monto1,
    monto2,
    monto2Calculado,
    password,
    cargandoMedios,
    procesando,
    error,
    total,
    abrir,
    cerrar,
    confirmar,
    seleccionarPorIndice,
    setMedioSeleccionado,
    setMedioSeleccionado2,
    cambiarMonto1,
    cambiarMonto2,
    alternarDivision,
    setPassword,
  };
}
